import React, { useState, useContext, useMemo } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { translate as t } from 'hooks/useTranslations';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { AppContext } from 'views/App/context';
import { useDTBPackageContext } from '../../context';
import useManageDTBPackages from '../../_data_source/useManageDTBPackage';
import { DTB } from 'views/App/Portal/DTB/_types/dtb';
import SelectedDtbsTab from './tabs/SelectedDtbsTab';
import { enqueueSnackbar } from 'notistack';
import AvailableDtbsTab from './tabs/AvailableDtbsTab';
import Tabs from '_lex/_atoms/Tabs';
import { useQueryClient } from 'react-query';

const SearchInLibrary: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganizationId } = useContext(AppContext);
  const { editedData, setEditedData, isAddingDtbs, setIsAddingDtbs } = useDTBPackageContext();
  const { addDtbs } = useManageDTBPackages();
  const queryClient = useQueryClient();

  const [selectedDtbs, setSelectedDtbs] = useState<DTB[]>([]);
  const [activeTab, setActiveTab] = useState(0);

  const packageDtbs = editedData?.dtbs || [];
  const existingDtbIds = useMemo(() => packageDtbs.map((dtb: { id: any }) => dtb.id), [packageDtbs]);

  // Filter out DTBs that are already in the package
  const filteredSelectedDtbs = useMemo(
    () => selectedDtbs.filter((dtb) => !existingDtbIds.includes(dtb.id)),
    [selectedDtbs, existingDtbIds]
  );

  const handleAddDtbsToPackage = async () => {
    if (!editedData?.id || filteredSelectedDtbs.length === 0) return;

    setIsAddingDtbs(true);
    try {
      await addDtbs(
        currentOrganizationId,
        editedData.id,
        filteredSelectedDtbs.map((dtb) => ({ id: dtb.id }))
      );

      setEditedData((prev: any) => ({
        ...prev,
        dtbs: [
          ...(prev.dtbs || []),
          ...filteredSelectedDtbs.map((dtb) => ({
            id: dtb.id,
            name: dtb.name,
            manual: dtb.manual,
            createdAt: dtb.createdAt,
            status: dtb.status,
          })),
        ],
      }));

      // Invalidate queries to refresh data
      queryClient.invalidateQueries(['dtbPackage', currentOrganizationId, editedData.id]);
      queryClient.invalidateQueries(['dtbPackages']);

      enqueueSnackbar(t('dtbPackages.addDtbs.success', 'DTBs added successfully'), {
        variant: 'success',
      });

      navigate(`../${editedData.id}`);
    } catch (error) {
      console.error('Error adding DTBs to package:', error);
      enqueueSnackbar(t('dtbPackages.addDtbs.error', 'Failed to add DTBs'), {
        variant: 'error',
      });
    } finally {
      setIsAddingDtbs(false);
    }
  };

  const selectedCount = filteredSelectedDtbs.length;

  return (
    <TakeoverLayout
      pageTitle={t('dtbPackages.searchInLibrary.title', 'Search in the library')}
      headerProps={{
        title: t('dtbPackages.searchInLibrary.title', `Adding to ${editedData?.name}`),
        subtitle: t('dtbPackages.searchInLibrary.subtitle', 'Select DTBs to add to the package'),
      }}
      backUrl={editedData?.id ? `../${editedData.id}` : '..'}
      contentHeaderLeft={
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(Number(newValue))}
          items={[
            {
              label: t('dtbPackages.searchInLibrary.tabs.available', 'All DTBs'),
              value: 0,
            },
            {
              label: t('dtbPackages.searchInLibrary.tabs.selected', 'Selected DTBs'),
              value: 1,
              count: selectedCount,
            },
          ]}
        />
      }
    >
      <Box sx={{ display: 'grid', gridTemplateRows: '1fr', gap: 4, minHeight: 0 }}>
        {activeTab === 0 ? (
          <AvailableDtbsTab
            selectedDtbs={selectedDtbs}
            setSelectedDtbs={setSelectedDtbs}
            onContinue={() => setActiveTab(1)}
            continueDisabled={filteredSelectedDtbs.length === 0}
            manualId={editedData?.manual?.id}
            existingPackageDtbs={packageDtbs}
          />
        ) : (
          <SelectedDtbsTab
            selectedDtbs={filteredSelectedDtbs}
            packageDtbs={packageDtbs}
            setSelectedDtbs={setSelectedDtbs}
            onAddDtbs={handleAddDtbsToPackage}
            addDisabled={filteredSelectedDtbs.length === 0 || isAddingDtbs}
            isAddingDtbs={isAddingDtbs}
          />
        )}
      </Box>
    </TakeoverLayout>
  );
};

export default SearchInLibrary;
